/**
 * localResponder.js
 *
 * Deterministic offline fallback for Kevia.
 * Returns contextually aware pastoral responses when the LLM backend
 * is unavailable — no network required.
 *
 * Response design:
 *  - Short for urgent/sensitive situations (pastoral care is presence, not words)
 *  - Deeper for reflective/planning contexts
 *  - Always grounded in real entity data and insights
 *  - Never pretends to be an LLM
 */

import { normalizePastoralTone } from "../assistant-tone";

/* ── Offline indicator — used only when insight context is thin ──────────── */
const OFFLINE_NOTE =
  "\n\n_Respondiendo desde información local. Análisis avanzados disponibles con conexión._";

/* ── Insight prose builder ───────────────────────────────────────────────── */

function topInsightLine(insights) {
  const top = insights
    .filter((i) => i.severity === "high" || i.severity === "critical")
    .slice(0, 1)[0];
  return top ? top.explanation : null;
}

/* ── Entity-aware response builders ─────────────────────────────────────── */

function personResponse(ctx, insights, msg) {
  const signal = topInsightLine(insights);
  const { name, urgency } = ctx;

  if (urgency === "high" || urgency === "critical") {
    if (signal) {
      return `Para ${name}: ${signal}. Sería un buen momento para planificar una visita cercana o un contacto directo.`;
    }
    return `${name} tiene señales que merecen atención pastoral próxima. ¿Hay algo específico en lo que te pueda ayudar a preparar?`;
  }

  if (msg.includes("visita") || msg.includes("visitar")) {
    return `Para preparar una visita con ${name}, ayuda recordar el contexto familiar, el estado espiritual y cualquier situación activa. ¿Quieres que te oriente en los próximos pasos?`;
  }

  if (signal) {
    return `Sobre ${name}: ${signal}. Cuéntame qué tienes en mente para orientarte mejor.`;
  }

  return `Puedo ayudarte con el seguimiento de ${name} desde la información disponible aquí. ¿Qué necesitas?`;
}

function familyResponse(ctx, insights, msg) {
  const signal = topInsightLine(insights);
  const { name, urgency } = ctx;

  if (urgency === "high" || urgency === "critical") {
    return signal
      ? `La familia ${name}: ${signal}. Te recomiendo priorizar una visita próxima.`
      : `La familia ${name} aparece con señales que merecen atención pastoral cercana.`;
  }

  if (msg.includes("visita") || msg.includes("seguimiento")) {
    return `Para la familia ${name}, puedo orientarte en la planificación de visita o en el seguimiento pastoral. ¿En qué área te enfocas?`;
  }

  return signal
    ? `Sobre la familia ${name}: ${signal}. ¿En qué te puedo ayudar?`
    : `Puedo ayudarte con orientación pastoral para la familia ${name} desde la información local disponible.`;
}

function churchResponse(ctx, insights) {
  const signal = topInsightLine(insights);
  const { name, urgency } = ctx;

  if (urgency === "high") {
    return signal
      ? `La iglesia ${name}: ${signal}. Sería importante coordinar una visita en los próximos días.`
      : `La iglesia ${name} tiene señales pastorales que merecen atención.`;
  }

  return signal
    ? `Iglesia ${name}: ${signal}. Puedo ayudarte con análisis de familias, visitas o planificación desde los datos disponibles.`
    : `Puedo orientarte sobre la iglesia ${name} con la información guardada en este dispositivo.`;
}

function visitResponse(ctx, insights) {
  const { name, urgency } = ctx;
  const signal = topInsightLine(insights);

  if (urgency === "high" || urgency === "critical") {
    return `Esta visita con ${name} requiere seguimiento. ${signal ? signal + "." : ""} ¿Quieres ayuda para programar el próximo contacto?`.trim();
  }

  return `Puedo ayudarte a reflexionar sobre esta visita con ${name} y planificar próximos pasos desde la información local.`;
}

function prayerResponse(ctx, insights) {
  const { name } = ctx;
  const signal = topInsightLine(insights);

  if (signal?.includes("respondida") || signal?.includes("gratitud")) {
    return `Esta petición de oración fue respondida — un momento para la gratitud. ¿Hay algo más en lo que te pueda acompañar?`;
  }

  if (signal?.includes("urgente")) {
    return `Esta petición activa por ${name} merece acompañamiento cercano. Estoy disponible para ayudarte a reflexionar o planificar el seguimiento.`;
  }

  return `Puedo ayudarte a reflexionar sobre esta petición de oración y su seguimiento pastoral.`;
}

function generalResponse() {
  return `Puedo ayudarte con orientación pastoral, planificación de visitas y seguimiento desde la información disponible aquí. ¿En qué te puedo acompañar?`;
}

/* ─────────────────────────────────────────────────────────────
   PUBLIC API
───────────────────────────────────────────────────────────── */

export function getLocalResponse(entityContext, userMessage) {
  const type     = entityContext?.type ?? "default";
  const insights = entityContext?.insights ?? [];
  const ctx      = {
    name:    entityContext?.name    ?? "esta persona",
    urgency: entityContext?.urgency ?? "none",
  };

  let body;
  switch (type) {
    case "person":  body = personResponse(ctx, insights, userMessage);  break;
    case "family":  body = familyResponse(ctx, insights, userMessage);  break;
    case "church":  body = churchResponse(ctx, insights);               break;
    case "visit":   body = visitResponse(ctx, insights);                break;
    case "prayer":  body = prayerResponse(ctx, insights);               break;
    default:        body = generalResponse();
  }

  // Only append offline note when there's no insight-driven content (generic responses)
  const hasSignal = insights.some((i) => i.severity === "high" || i.severity === "critical");
  const full      = hasSignal ? body : body + OFFLINE_NOTE;

  return normalizePastoralTone(full);
}
